import React, { useState, useEffect, useCallback } from "react";
import { db } from "./firebaseConfig";
import { collection, getDocs, doc, setDoc, query, where } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import "./CreateQuiz.css"; // Updated styles
import "./ButtonStyles.css"; // If you still need this for the gradient-border-button

const CreateQuiz = ({ user }) => {
  const [subjects, setSubjects] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState("");
  const [questions, setQuestions] = useState([]);
  const [filteredQuestions, setFilteredQuestions] = useState([]);
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [quizCode, setQuizCode] = useState("");
  const [defaultTimeLimit, setDefaultTimeLimit] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [filters, setFilters] = useState({
    topic: "",
    level: "",
    specCode: "",
  });

  const navigate = useNavigate();

  // Fetch subjects on mount
  useEffect(() => {
    const fetchSubjects = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "questions"));
        const subjectsSet = new Set();
        querySnapshot.forEach((doc) => {
          const subject = doc.data().subject;
          if (subject) subjectsSet.add(subject);
        });
        setSubjects(Array.from(subjectsSet).sort());
      } catch (error) {
        console.error("Error fetching subjects: ", error.message);
      }
    };

    fetchSubjects();
  }, []);

  // Fetch questions for selected subject
  const fetchQuestionsBySubject = async (subject) => {
    try {
      const q = query(
        collection(db, "questions"),
        where("subject", "==", subject)
      );
      const querySnapshot = await getDocs(q);
      const fetchedQuestions = querySnapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));
      setQuestions(fetchedQuestions);
      setFilteredQuestions(fetchedQuestions);
    } catch (error) {
      console.error("Error fetching questions: ", error.message);
    }
  };

  // Fetch questions when subject is selected
  useEffect(() => {
    if (selectedSubject) {
      fetchQuestionsBySubject(selectedSubject);
    }
  }, [selectedSubject]);

  // Handle subject selection
  const handleSubjectSelect = (subject) => {
    setSelectedSubject(subject);
    // Reset filters when changing subject
    setFilters({ topic: "", level: "", specCode: "" });
    // Clear selected questions when changing subject
    setSelectedQuestions([]);
  };

  // Handle filter changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    const updatedFilters = { ...filters, [name]: value };
    setFilters(updatedFilters);

    const filtered = questions.filter((question) => {
      const topicMatch =
        !updatedFilters.topic ||
        question.topic?.toLowerCase().includes(updatedFilters.topic.toLowerCase());

      const levelMatch =
        !updatedFilters.level ||
        question.level?.toLowerCase().includes(updatedFilters.level.toLowerCase());

      const specMatch =
        !updatedFilters.specCode ||
        question.specCode?.toLowerCase().includes(updatedFilters.specCode.toLowerCase());

      return topicMatch && levelMatch && specMatch;
    });
    setFilteredQuestions(filtered);
  };

  // Handle question selection
  const handleQuestionSelect = (id) => {
    setSelectedQuestions((prev) =>
      prev.includes(id) ? prev.filter((qid) => qid !== id) : [...prev, id]
    );
  };

  // Move a question up in the order
  const moveQuestionUp = (index) => {
    if (index === 0) return; // Can't move up if already at the top
    
    setSelectedQuestions(prev => {
      const newOrder = [...prev];
      // Swap with the previous item
      [newOrder[index], newOrder[index - 1]] = [newOrder[index - 1], newOrder[index]];
      return newOrder;
    });
  };

  // Move a question down in the order
  const moveQuestionDown = (index) => {
    if (index === selectedQuestions.length - 1) return; // Can't move down if already at the bottom
    
    setSelectedQuestions(prev => {
      const newOrder = [...prev];
      // Swap with the next item
      [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
      return newOrder;
    });
  };

  // Get question details by ID
  const getQuestionById = useCallback((id) => {
    return filteredQuestions.find(q => q.id === id) || null;
  }, [filteredQuestions]);

  // Generate a random quiz code
  const generateRandomCode = () => {
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < 6; i++) {
      result += characters.charAt(Math.floor(Math.random() * characters.length));
    }
    setQuizCode(result);
  };

  // Handle quiz creation
  const handleCreateQuiz = async () => {
    if (!quizCode) {
      alert("Please enter a Quiz Code!");
      return;
    }
    if (selectedQuestions.length === 0) {
      alert("Please select at least one question!");
      return;
    }
    if (!defaultTimeLimit) {
      alert("Please enter a default time limit (in minutes)!");
      return;
    }

    const timeLimitInSeconds = parseInt(defaultTimeLimit, 10) * 60;
    if (isNaN(timeLimitInSeconds) || timeLimitInSeconds <= 0) {
      alert("Please enter a valid time limit in minutes!");
      return;
    }

    setIsCreating(true); // Start loading state

    try {
      // Check if quiz code already exists
      const q = query(collection(db, "quizzes"), where("quizCode", "==", quizCode));
      const snapshot = await getDocs(q);
      
      if (!snapshot.empty) {
        alert("Quiz code already exists. Please choose a unique code.");
        setIsCreating(false);
        return;
      }

      const newQuiz = {
        quizCode,
        questionIds: selectedQuestions,
        createdAt: new Date(),
        defaultTimeLimit: timeLimitInSeconds,
        subject: selectedSubject, // Add subject to the quiz
        creatorUid: user.uid, // Add creator's UID to the quiz
      };

      // Use setDoc with quizCode as document ID for guaranteed uniqueness
      const docRef = doc(db, "quizzes", quizCode);
      await setDoc(docRef, newQuiz);

      // Pass selected question details to the success page
      const selectedQuestionDetails = filteredQuestions.filter((question) =>
        selectedQuestions.includes(question.id)
      );

      // Navigate to success page with quiz details
      navigate("/quiz-success", {
        state: { quizCode, selectedQuestions: selectedQuestionDetails },
      });

      // Clear state
      setQuizCode("");
      setSelectedQuestions([]);
      setDefaultTimeLimit("");
    } catch (error) {
      console.error("Error creating quiz: ", error.message);
      alert("Failed to create quiz. Please try again.");
    } finally {
      setIsCreating(false); // End loading state
    }
  };

  // Clear filters
  const clearFilters = () => {
    setFilters({ topic: "", level: "", specCode: "" });
    setFilteredQuestions(questions);
  };

  return (
    <div className="create-quiz-container">
      <header className="create-quiz-header">
        <h1>Create New Quiz</h1>
        <button className="back-button" onClick={() => navigate("/dashboard")}>
          Back to Dashboard
        </button>
      </header>

      <hr className="divider" />

      <main className="create-quiz-main">
        {/* Quiz Settings Card */}
        <section className="quiz-settings-card card">
          <div className="quiz-settings-content">
            <div className="quiz-settings">
              <h2>Quiz Settings</h2>
              <div className="form-group">
                <label htmlFor="quizCode">Quiz Code</label>
                <div className="input-with-button">
                  <input
                    id="quizCode"
                    type="text"
                    placeholder="Enter unique quiz code"
                    value={quizCode}
                    onChange={(e) => setQuizCode(e.target.value)}
                  />
                  <button 
                    type="button" 
                    className="generate-code-button"
                    onClick={generateRandomCode}
                    title="Generate random code"
                  >
                    Generate
                  </button>
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="timeLimit">Time Limit (minutes)</label>
                <input
                  id="timeLimit"
                  type="number"
                  placeholder="Enter time in minutes"
                  value={defaultTimeLimit}
                  onChange={(e) => setDefaultTimeLimit(e.target.value)}
                />
              </div>
            </div>
          </div>
        </section>
            
        {/* Subject Selection Card */}
        <section className="subject-selection-card card">
          <h2>Select a Subject</h2>
          <div className="subject-buttons">
            {subjects.map((subject) => (
              <button
                key={subject}
                onClick={() => handleSubjectSelect(subject)}
                className={selectedSubject === subject ? "subject-button selected" : "subject-button"}
              >
                {subject}
              </button>
            ))}
          </div>
        </section>

        {/* Filters Card - Only show if a subject is selected */}
        {selectedSubject && (
          <section className="filters-card card">
            <h2>Filter Questions</h2>
            <div className="filter-inputs">
              <div className="form-group">
                <label htmlFor="topic">Topic</label>
                <input
                  id="topic"
                  type="text"
                  name="topic"
                  placeholder="Filter by topic"
                  value={filters.topic}
                  onChange={handleFilterChange}
                />
              </div>
              <div className="form-group">
                <label htmlFor="level">Level</label>
                <input
                  id="level"
                  type="text"
                  name="level"
                  placeholder="Filter by level"
                  value={filters.level}
                  onChange={handleFilterChange}
                />
              </div>
              <div className="form-group">
                <label htmlFor="specCode">Spec Code</label>
                <input
                  id="specCode"
                  type="text"
                  name="specCode"
                  placeholder="Filter by spec code"
                  value={filters.specCode}
                  onChange={handleFilterChange}
                />
              </div>
            </div>
            <button className="clear-filters-button" onClick={clearFilters}>
              Clear Filters
            </button>
          </section>
        )}

        {/* Only show questions table if a subject is selected */}
        {selectedSubject && (
          <section className="questions-table-card card">
            <div className="questions-header">
              <h2>Questions for {selectedSubject} ({filteredQuestions.length})</h2>
              <span className="selected-count">
                {selectedQuestions.length} question(s) selected
              </span>
            </div>
            <div className="table-responsive">
              <table className="questions-table">
                <thead>
                  <tr>
                    <th>
                      <div className="select-all-container">
                        <input
                          type="checkbox"
                          onChange={(e) => {
                            if (e.target.checked) {
                              // Select all questions
                              setSelectedQuestions(filteredQuestions.map(q => q.id));
                            } else {
                              // Deselect all questions
                              setSelectedQuestions([]);
                            }
                          }}
                          checked={
                            filteredQuestions.length > 0 &&
                            selectedQuestions.length === filteredQuestions.length
                          }
                        />
                        <span className="select-all-label">Select All</span>
                      </div>
                    </th>
                    <th>Question</th>
                    <th>Spec Code</th>
                    <th>Topic</th>
                    <th>Level</th>
                    <th>Max Mark</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredQuestions.length > 0 ? (
                    filteredQuestions.map((question) => (
                      <tr
                        key={question.id}
                        className={
                          selectedQuestions.includes(question.id) ? "selected" : ""
                        }
                      >
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedQuestions.includes(question.id)}
                            onChange={() => handleQuestionSelect(question.id)}
                          />
                        </td>
                        <td>
                          {question.questionText ? (
                            <div>{question.questionText}</div>
                          ) : question.questionImageURL ? (
                            <a
                              href={question.questionImageURL}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <img
                                src={question.questionImageURL}
                                alt="Question"
                                className="question-image"
                              />
                            </a>
                          ) : (
                            <span className="no-question">No Question Provided</span>
                          )}
                        </td>
                        <td>{question.specCode || "-"}</td>
                        <td>{question.topic || "-"}</td>
                        <td>{question.level || "-"}</td>
                        <td>{question.maxMark || "-"}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="no-data">
                        No questions match your filter criteria.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="create-quiz-btn-container">
              <button
                onClick={handleCreateQuiz}
                className="gradient-border-button create-quiz-button"
                disabled={selectedQuestions.length === 0 || isCreating}
              >
                {isCreating ? "Creating..." : "Create Quiz"}
              </button>
            </div>
          </section>
        )}

        {/* Selected Questions Order Section - Only show if questions are selected */}
        {selectedSubject && selectedQuestions.length > 0 && (
          <section className="selected-questions-card card">
            <h2>Question Order</h2>
            <p className="order-instructions">
              Use the up and down buttons to arrange the questions in the order you want them to appear in the quiz.
            </p>
            <div className="selected-questions-list">
              {selectedQuestions.map((questionId, index) => {
                const question = getQuestionById(questionId);
                if (!question) return null;
                
                return (
                  <div key={questionId} className="selected-question-item">
                    <div className="question-order-number">{index + 1}</div>
                    <div className="question-content">
                      {question.questionText ? (
                        <div className="question-text">{question.questionText}</div>
                      ) : question.questionImageURL ? (
                        <div className="question-image-container">
                          <img
                            src={question.questionImageURL}
                            alt="Question"
                            className="question-thumbnail"
                          />
                          <span className="image-indicator">[Image Question]</span>
                        </div>
                      ) : (
                        <span className="no-question">No Question Provided</span>
                      )}
                      <div className="question-meta">
                        <span className="question-topic">{question.topic || "-"}</span>
                        <span className="question-spec">{question.specCode || "-"}</span>
                        <span className="question-marks">{question.maxMark || "-"} marks</span>
                      </div>
                    </div>
                    <div className="question-actions">
                      <button 
                        className="move-button up-button" 
                        onClick={() => moveQuestionUp(index)}
                        disabled={index === 0}
                        title="Move question up"
                      >
                        ↑
                      </button>
                      <button 
                        className="move-button down-button" 
                        onClick={() => moveQuestionDown(index)}
                        disabled={index === selectedQuestions.length - 1}
                        title="Move question down"
                      >
                        ↓
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}
      </main>
    </div>
  );
};

export default CreateQuiz;
